import { loadCalendar } from './utils/consultation/calendar';
import { initCaptchaForForm } from './utils/captcha-v2';
import serializeForm from './utils/consultation/serialize-form';
import { postToLeadWithCaptcha } from './utils/post-to-lead-with-captcha';
import { slideDown, slideUp } from './startup/slide';
import { validate } from './utils/validation';
import attachTrackingPayload from './utils/tracking-payload';
import { getAgeFromDoB, getFormattedDate, getFormattedTime } from './utils/date';
import { getCategoryFromInterest, getLeadDimensions, getPhoneLeadDimensions, getPageDimensions } from './utils/ga4-tracking/custom-dimensions';
import { pushToDataLayer } from './utils/ga4-tracking/push-to-data-layer';
import { metrics } from '@sentry/browser';
import { pageSettings } from './utils/ga4-tracking/page-settings';
import { getPreviousAppointment, setPreviousAppointment } from './utils/consultation/appointment-service';
import dayjs from 'dayjs';
import { AppointmentStatus } from '../types/consultation';
import { setCookie } from './startup/cookies-helper';
import FormMicroLibrary from './forms/form-library';
import { debounce } from './utils/utils';

const validInterests = ['comprehensive', 'civil-servant', 'dental', 'clinic'];
const validEmployments = ['employed', 'self-employed', 'student', 'civil-servant', 'civil-servant-applicant'];

const urlParams = new URLSearchParams(window.location.search);
const interestDefault = (validInterests.includes(urlParams.get('interest')) && urlParams.get('interest')) || pageSettings.interest || 'comprehensive';
const employmentDefault = (validEmployments.includes(urlParams.get('employment')) && urlParams.get('employment')) || pageSettings.jobStatus || 'employed';

const defaults = {
  interest: pageSettings.isExpat && interestDefault === 'comprehensive' ? 'comprehensive-expat' : interestDefault,
  employment: employmentDefault
};

const noCal = window.nocal;
const blockedReferrer = !(window.refblock || []).every(referrer => !document.referrer.includes(referrer));
const isAppointmentBlocked = formData => {
  if (blockedReferrer) {
    return true;
  }

  if (!noCal) {
    return false;
  }
  const { employment, maxAge } = noCal;

  return !(
    (employment && formData.employment && employment !== formData.employment && !employment.includes(formData.employment)) ||
    (maxAge && formData.date_of_birth && maxAge >= getAgeFromDoB(formData.date_of_birth))
  );
};

document.querySelectorAll('.consultation-new').forEach(consultation => {
  const blockType = consultation.classList.contains('modal') ? 'consultation-modal' : 'consultation-block';
  const elementSection = `${blockType}${en ? '-english' : ''}`;
  const slidesContainer = consultation.querySelector('.slides-container');
  const interestSlide = consultation.querySelector('.interest-slide');
  const personalSlide = consultation.querySelector('.personal-slide');
  const appointmentSlide = consultation.querySelector('.appointment-slide');
  const resultSlide = consultation.querySelector('.result-slide');
  const rejectedEnSlide = consultation.querySelector('.rejected-en-slide');
  const rejectedStudentSlide = consultation.querySelector('.rejected-student-slide');
  const rejectedTopupSlide = consultation.querySelector('.rejected-topup-slide');
  const rejectedTooYoungSlide = consultation.querySelector('.rejected-too-young-slide');
  const bookingFailedSlide = consultation.querySelector('.booking-failed-slide');

  const consultationForm = consultation.querySelector('form');
  const appointmentGroup = appointmentSlide.querySelector('.appointment-group');
  const languageSelect = appointmentSlide.querySelector('.language-select');
  const dateSelect = consultationForm.querySelector('.dateSelect');
  const timeSelect = consultationForm.querySelector('.timeSelect');
  const languageSelectRadios = consultationForm.querySelectorAll('input[type="radio"][name="consultation_language"]');

  const employedRadio = personalSlide.querySelector('input[name="employment"][value="employed"]');
  const civilServantRadio = personalSlide.querySelector('input[name="employment"][value="civil-servant"]');
  const professionalStatusGroup = consultationForm.querySelector('.professional-status-group');

  const incomeDefaultRadio = personalSlide.querySelector('input[name="income"][value=""]');
  const lessRadio = personalSlide.querySelector('input[name="income"][value="less"]');
  const incomeGroup = consultationForm.querySelector('.income-group');

  const arrowSvg = '<svg viewBox="0 0 16 16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h10.5M10.5 5l3 3-3 3"></path></svg>';

  let submitted = false;
  let allowCallbackOnly = false;
  let failedToFetchAppointments = false;

  const formLibrary = new FormMicroLibrary(
    slidesContainer,
    (_, nextSlide, submitted) => {
      if (submitted) {
        return false;
      }
      fireGa4Event(nextSlide);
    },
    submitted
  );
  const notificationHandler = formLibrary.getNotificationHandler();

  let calendarType = consultationForm.dataset.consultation;
  let customerType = null;

  let userProfile = {};
  let previousAppointmentEntryPromise;

  const defaultInterestInput = consultation.querySelector(`input[name="interest"][value="${defaults.interest}"]`);
  const defaultEmploymentInput = consultation.querySelector(`input[name="employment"][value="${defaults.employment}"]`);

  defaultInterestInput && (defaultInterestInput.checked = true);
  defaultEmploymentInput && (defaultEmploymentInput.checked = true);

  const getFromStepIndex = activeFormSlide => {
    switch (activeFormSlide) {
      case interestSlide:
        return 1;
      case personalSlide:
        return 2;
      case appointmentSlide:
        return 3;
      default:
        return 4;
    }
  };

  const fireGa4Event = activeSlide => {
    const formStepIndex = getFromStepIndex(activeSlide);
    pushToDataLayer({
      event: 'form_view',
      form_step: formStepIndex,
      form_steps_total: 4,
      element_section: elementSection,
      ...getLeadDimensions(userProfile)
    });
  };

  // change slides to call me back only
  const boxHeight = () => {
    slidesContainer.style.height = formLibrary.currentSlide.offsetHeight + 'px';
  };

  const submit = appointmentSlide.querySelector('.btn-submitter');
  const captchaMountElement = consultationForm.querySelector('.captaincaps');
  const appointmentEmailInput = appointmentSlide?.querySelector('.emailSelect') || consultationForm.querySelector('input[name="email"]');
  let captchaController;
  const heading = appointmentGroup.querySelector('.heading-label');

  let isEmailRejected = false;
  let isIncomeTooLow = false;
  let errorOnSubmit = false;

  const originalHeadingText = heading.textContent;
  let originalSubmitContent = submit.innerHTML;

  const updateRejected = rejected => {
    if (rejected) {
      heading.textContent = en ? 'Sorry, there are no more dates available.' : 'Leider stehen keine Termine mehr zur Verfügung.';
      submit.innerHTML = en ? 'Call me back instead' : 'Stattdessen Rückruf anfordern';
    } else {
      heading.textContent = originalHeadingText;
      submit.innerHTML = originalSubmitContent;
    }
    appointmentGroup.classList.toggle('is-disabled', rejected);
    isEmailRejected = rejected;
  };

  const checkForCallbackAndRejection = formValues => {
    const { income, employment } = formValues;
    //Auto Reject for comprehensive under Bemessungsgrenze, Penioners, Unemployed, students above 39 with no income

    // Employment rejections
    const employmentRejections = employment === 'pensioner' || employment === 'unemployed';

    // Age rejections
    const rejectedTooYoung =
      getAgeFromDoB(formValues.date_of_birth) < 16 ||
      (getAgeFromDoB(formValues.date_of_birth) < 17 && employment !== 'civil-servant-applicant') ||
      (getAgeFromDoB(formValues.date_of_birth) < 18 && !['student', 'civil-servant-applicant'].includes(employment));

    const studentRejected = getAgeFromDoB(formValues.date_of_birth) > 37 && employment === 'student';

    // Income rejecttions
    const rejectedByIncome = (['less', '30to65'].includes(income) && employment === 'employed') || (income === 'less' && employment === 'self-employed');

    // Callback cases
    const callBackOnlyByAge = getAgeFromDoB(formValues.date_of_birth) > 49 || (getAgeFromDoB(formValues.date_of_birth) > 37 && employment === 'civil-servant-applicant');

    // Check with appointment blocked settings from page
    allowCallbackOnly = isAppointmentBlocked(formValues) || callBackOnlyByAge || failedToFetchAppointments;

    const enLeadRejected = employmentRejections || rejectedByIncome;

    if (![enLeadRejected, rejectedTooYoung, studentRejected].some(rejection => rejection)) {
      const reportCard = document.querySelector('.report-card');
      const appointmentCard = document.querySelector('.appointment-card');
      const movingFormFields = document.querySelector('.moving-form-groups');
      if (allowCallbackOnly) {
        reportCard.append(movingFormFields);
        submit.innerHTML = `${en ? 'Call me back' : 'Rückruf anfordern'} ${arrowSvg}`;
      } else {
        appointmentCard.append(movingFormFields);
        submit.innerHTML = originalSubmitContent;
      }
      appointmentCard.classList.toggle('is-hidden', allowCallbackOnly);
      appointmentGroup.classList.toggle('is-hidden', allowCallbackOnly);
      isIncomeTooLow = rejectedByIncome;
    }
    return { enLeadRejected, rejectedTooYoung, studentRejected };
  };

  const updateResultSlide = (response, isAlreadyBooked = false) => {
    const appointmentInformation = response?.appointment_details;
    const alreadyBooked = !!isAlreadyBooked;
    const newConsultation = !alreadyBooked;
    const isAppointment = !!appointmentInformation?.status;
    const isCallback = !isAppointment;
    const doiConfirmed = appointmentInformation?.status === 'confirmed' || response?.consent_confirmed;
    const doiPending = !doiConfirmed;
    const doiRequiredPending = isAppointment && appointmentInformation?.doi_enabled && !doiConfirmed;
    const doiNotRequiredPending = !doiRequiredPending;
    const doiOptionalPending = !appointmentInformation?.doi_enabled && !doiConfirmed;

    // Apply visibility logic based on conditions
    const visibilityConditions = {
      'data-already-booked': alreadyBooked,
      'data-new-consultation': newConsultation,
      'data-is-appointment': isAppointment,
      'data-is-callback': isCallback,
      'data-doi-confirmed': doiConfirmed,
      'data-doi-pending': doiPending,
      'data-doi-required-pending': doiRequiredPending,
      'data-doi-not-required-pending': doiNotRequiredPending,
      'data-doi-optional-pending': doiOptionalPending
    };

    const elementsToToggle = resultSlide.querySelectorAll(
      Object.keys(visibilityConditions)
        .map(key => `[${key}]`)
        .join(', ')
    );

    elementsToToggle.forEach(element => {
      let shouldShow = true;

      for (const [attr, value] of Object.entries(visibilityConditions)) {
        if (element.hasAttribute(attr) && !value) {
          shouldShow = false;
          break;
        }
      }

      element.classList.toggle('is-hidden', !shouldShow);
    });

    if (doiRequiredPending && alreadyBooked) {
      const expiresIn = dayjs(appointmentInformation.created_at).add(60, 'minutes').diff(dayjs(), 'minutes');
      const timer = resultSlide.querySelector('.timer');
      if (timer) timer.innerHTML = `<p class="heading timer">Bitte bestätige deine E-Mail in den nächsten <span class="minutes">${expiresIn} Minuten</span></p>`;
    }

    if (isAppointment) {
      const date = dayjs(appointmentInformation.start);
      resultSlide.querySelector('#booked-date').innerHTML = getFormattedDate(date).replace(' dem', '');
      resultSlide.querySelector('#booked-time').innerHTML = getFormattedTime(date);
    }

    const emailDomains = ['gmail', 'googlemail', 'outlook', 'web', 'gmx'];
    const emailDomain = appointmentSlide.querySelector('.emailSelect').value.split('@')[1];

    if (doiPending && emailDomain && emailDomains.some(domain => emailDomain.includes(domain))) {
      resultSlide.querySelector('.email-buttons-container').classList.add('email-match');
      resultSlide.querySelectorAll('.email-btns > a').forEach(btn => btn.classList.toggle('is-hidden', !btn.dataset.emailDomain.split(',').some(domain => emailDomain.includes(domain))));
    }
  };

  const onCaptchaSuccess = () => {
    refreshSubmitState();
  };

  const onCaptchaError = err => {
    setCookie('traffic_type', 'spam', 365);
    pushToDataLayer({
      event: 'spam_lead',
      element_section: 'consultation_modal',
      traffic_type: 'spam',
      error_type: 'captcha_error',
      error_message: err
    });
    originalSubmitContent = 'ERROR';
    submit.innerText = 'ERROR';
  };

  let requireCaptchaOnSubmit = false;

  const hasVisibleRequiredFields = () => {
    const requiredFields = Array.from(appointmentSlide.querySelectorAll('[required]'));

    return requiredFields.every(field => {
      if (!(field instanceof HTMLElement) || field.disabled) {
        return true;
      }

      const hiddenByClass = field.classList.contains('is-hidden') || field.closest('.is-hidden');
      if (hiddenByClass) {
        return true;
      }

      if (allowCallbackOnly && field.classList.contains('dateSelect')) {
        return true;
      }

      if (allowCallbackOnly && field.classList.contains('timeSelect')) {
        return true;
      }

      if ('checkValidity' in field && typeof field.checkValidity === 'function') {
        return field.checkValidity();
      }

      return true;
    });
  };

  const refreshSubmitState = () => {
    const hasCaptchaToken = !requireCaptchaOnSubmit || Boolean(captchaController?.hasUsableToken());
    const canSubmit = Boolean(hasVisibleRequiredFields() && hasCaptchaToken && !isEmailRejected && !submitted);

    submit.disabled = !canSubmit;
    submit.classList.toggle('active', canSubmit);
    submit.classList.toggle('disabled', !canSubmit);
  };

  const { controller, requireCaptchaOnSubmit: capturedRequireCaptchaOnSubmit } = initCaptchaForForm({
    form: consultationForm,
    mountElement: captchaMountElement,
    emailInput: appointmentEmailInput,
    locale: en ? 'en' : 'de',
    onComplete: onCaptchaSuccess,
    onExpire: () => {
      refreshSubmitState();
    },
    onError: error => {
      const errorMessage = error?.detail || error?.code || 'captcha_error';
      onCaptchaError(errorMessage);
      refreshSubmitState();
    },
    onShowWidget: () => {
      captchaMountElement?.classList.remove('is-hidden');
      submit.classList.add('is-hidden');
      refreshSubmitState();
      boxHeight();
    },
    onHideWidget: () => {
      captchaMountElement?.classList.add('is-hidden');
      submit.classList.remove('is-hidden');
      refreshSubmitState();
      boxHeight();
    }
  });
  captchaController = controller;
  requireCaptchaOnSubmit = capturedRequireCaptchaOnSubmit;

  refreshSubmitState();

  //fire first ga4trackign event on modal open for modals
  // or on first interacton for page-blocks
  if (blockType === 'consultation-modal') {
    if (consultation.classList.contains('is-open')) {
      fireGa4Event(formLibrary.currentSlide);
    }
    // Use MutationObserver to detect when modal is opened (is-open class added)
    let isOpenEventFired = false;
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isNowOpen = consultation.classList.contains('is-open');
          // Fire event only when modal transitions from closed to open
          if (isNowOpen && !isOpenEventFired) {
            isOpenEventFired = true;
            fireGa4Event(formLibrary.currentSlide);
            boxHeight();
          } else if (!isNowOpen) {
            // Reset flag when modal is closed
            isOpenEventFired = false;
          }
        }
      });
    });
    observer.observe(consultation, { attributes: true, attributeFilter: ['class'] });
  } else {
    consultation.addEventListener(
      'click',
      () => {
        fireGa4Event(formLibrary.currentSlide);
      },
      { once: true, capture: true }
    );
  }

  // initialize the dom
  // functions in interest slide (slide 01)
  if (interestSlide) {
    const nextBtn = interestSlide.querySelector('.btn-next');
    const fieldset = interestSlide.querySelector('fieldset');
    nextBtn.addEventListener('click', ev => {
      ev.preventDefault();
      if (validate.validateAll(fieldset).length) {
        return;
      }
      const formValues = serializeForm(consultationForm);
      const interestValue = formValues.interest;
      const formElements = Array.from(professionalStatusGroup.querySelectorAll('.otto-form-element'));
      const civilServantEmployments = ['civil-servant-applicant', 'civil-servant'];
      const currentEmployment = formValues.employment;
      let isTopUp = false;

      switch (formValues.interest) {
        case 'civil-servant':
          professionalStatusGroup.classList.remove('is-hidden');

          formElements.forEach(element => {
            element.classList.toggle('is-hidden', !civilServantEmployments.includes(element.querySelector('input').value));
          });
          if (civilServantEmployments.includes(currentEmployment)) {
            break;
          }
          if (civilServantEmployments.includes(defaults.employment)) {
            professionalStatusGroup.querySelector(`input[value=${defaults.employment}]`).checked = true;
            break;
          }
          civilServantRadio.checked = true;
          break;
        case 'dental':
        case 'clinic':
          isTopUp = true;
          updateRejectedTopUpSlide(formValues.interest);
          return;
        default:
          professionalStatusGroup.classList.remove('is-hidden');
          professionalStatusGroup.querySelectorAll('.otto-form-element.is-hidden').forEach(element => element.classList.remove('is-hidden'));
          if (currentEmployment && !civilServantEmployments.includes(currentEmployment)) {
            break;
          }
          if (!civilServantEmployments.includes(defaults.employment)) {
            professionalStatusGroup.querySelector(`input[value=${defaults.employment}]`).checked = true;
            break;
          }
          employedRadio.checked = true;
          break;
      }

      if (incomeGroup) {
        incomeGroup.classList.toggle('is-hidden', isTopUp);
        incomeDefaultRadio.checked = isTopUp;
        lessRadio.checked = !isTopUp;
      }

      // Page translation can lead to english customers on german page
      const selectedLanguage = languageSelectRadios?.length ? Array.from(languageSelectRadios).find(radio => radio.checked)?.value : en ? 'en' : 'de';

      calendarType = selectedLanguage === 'en' ? 'sales-expat' : 'sales';

      userProfile = {
        ...userProfile,
        insurance_category: getCategoryFromInterest(interestValue)
      };

      handleCalendarLoad(calendarType, dateSelect, timeSelect);
      formLibrary.switchSlide(personalSlide);
    });
  }

  // functions in personal slide (slide 02)
  if (personalSlide) {
    const nextBtn = personalSlide.querySelector('.btn-next');
    const fieldset = personalSlide.querySelector('fieldset');
    const customerTypeCheckbox = personalSlide.querySelector('input[name="customer_type"]');

    nextBtn.addEventListener('click', ev => {
      ev.preventDefault();
      const validationResult = validate.validateAll(fieldset);
      if (validationResult.length) {
        notificationHandler.setValidateError(validationResult, nextBtn);
        return;
      }

      const formValues = serializeForm(consultationForm);
      const { income, employment, date_of_birth } = formValues;
      let incomeString;
      let jaeg_reached;
      if (customerTypeCheckbox) {
        customerType = customerTypeCheckbox.checked ? 'existing-customer' : 'new-customer';
      }

      const { enLeadRejected, rejectedTooYoung, studentRejected } = checkForCallbackAndRejection(formValues);

      if (income) {
        switch (income) {
          case 'more':
            incomeString = window.craftConfig.global.jaegReachedTracking;
            jaeg_reached = 'yes';
            break;
          case 'less':
            incomeString = '29999';
            jaeg_reached = 'no';
            break;
          default:
            incomeString = window.craftConfig.global.jaegNotReachedTracking;
            jaeg_reached = 'no';
        }
      }

      userProfile = {
        ...userProfile,
        // if a user selects a civil-servant employment a different category than "residual" was likely a mistake
        ...(['civil-servant', 'civil-servant-applicant'].includes(employment) && { insurance_category: 'residual' }),
        employment: employment || 'na',
        date_of_birth,
        ...(customerType && { customer_type: customerType }),
        ...(incomeGroup && { income: incomeString || 'na' }),
        ...(incomeGroup && { jaeg_reached: jaeg_reached !== undefined ? jaeg_reached : 'na' })
      };

      if (enLeadRejected) {
        updateRejectedSlide(formValues);
      } else if (studentRejected) {
        updateRejectedStudentSlide(formValues);
      } else if (rejectedTooYoung) {
        updateRejectedTooYoungSlide();
      } else {
        formLibrary.switchSlide(appointmentSlide);
      }
    });
  }

  // functions in appointment slide (slide 03)
  if (appointmentSlide) {
    const emailInput = appointmentSlide.querySelector('.emailSelect');
    const phoneInput = appointmentSlide.querySelector('[name="phone_number"]');
    let calendarIsLoading = false;
    let requestTimeout = 0;

    const alerts = {
      taken: appointmentSlide.querySelector('.alert.danger.taken'),
      email: appointmentSlide.querySelector('.alert.danger.email')
    };

    if (languageSelectRadios?.length) {
      languageSelectRadios.forEach(radio => {
        radio.addEventListener('change', async () => {
          if (failedToFetchAppointments || allowCallbackOnly || calendarIsLoading) {
            return;
          }

          if (!radio.checked) {
            return;
          }

          const newCalendarType = radio.value === 'en' ? 'sales-expat' : 'sales';
          if (newCalendarType === calendarType) {
            return;
          }

          calendarIsLoading = true;
          calendarType = newCalendarType;
          await handleCalendarLoad(calendarType, dateSelect, timeSelect, true, requestTimeout);
          calendarIsLoading = false;
          languageSelectRadios.forEach(radio => (radio.checked = (radio.value === 'en' && calendarType === 'sales-expat') || (radio.value === 'de' && calendarType === 'sales')));
          requestTimeout = Math.min(requestTimeout === 0 ? 100 : requestTimeout * 2, 20000);
        });
      });
    }

    const backBtn = appointmentSlide.querySelector('.btn-prev');
    if (backBtn) {
      backBtn.addEventListener('click', ev => {
        ev.preventDefault();
        refreshSubmitState();
      });
    }

    //codeblock for checking the email
    let checked = false;
    const checkEmail = () => {
      if (!isIncomeTooLow) {
        checked = true;
        const email = emailInput.value;
        const validation = validate.validate(emailInput);
        if (email.includes('@ottonova.de') && window.location.host.indexOf('ottonova.de') < 0) {
          isEmailRejected = true;
          submit.disabled = true;
          return;
        }
        if (email === '' || (validation && !validation.valid)) {
          return;
        }
        fetch(`${window.craftConfig.global.leadServiceUrl}/salesforce/was-rejected-in-prequalification`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: email })
        })
          .then(response => {
            return response.ok && (email.includes('REJECTED') || response.json());
          })
          .then(isEmailRejected => {
            updateRejected(isEmailRejected);
            //prefetch previous appointment info to speed up process
            previousAppointmentEntryPromise = getPreviousAppointment(email);
          })
          .catch(() => updateRejected(false));
      }
    };

    emailInput.addEventListener('animationstart', e => {
      if (e.animationName === 'onAutoFillStart') {
        setTimeout(() => {
          if (emailInput.value !== '' && !checked) {
            checkEmail();
          }
        }, 1000);
      }
    });

    const debouncedEmailCheck = debounce(eventType => {
      checkEmail();
      if (errorOnSubmit) {
        enableButton(eventType);
      }
      refreshSubmitState();
    }, 1500);

    emailInput.addEventListener('input', event => {
      debouncedEmailCheck(event.type);
    });

    phoneInput.addEventListener('input', () => {
      refreshSubmitState();
    });

    // Auto-start captcha widget when user begins typing in the email field
    emailInput.addEventListener(
      'input',
      () => {
        if (captchaController && !captchaController.hasUsableToken()) {
          captchaController.startIfNeeded();
        }
      },
      { once: true }
    );

    const bookingEventlistener = ev => {
      const { appointment, alreadyBooked } = ev.detail;
      const response = { appointment_details: appointment };
      updateResultSlide(response, alreadyBooked);
      formLibrary.switchSlide(resultSlide, false);
    };
    window.addEventListener('consultation-booked', bookingEventlistener);

    const notifyForms = (appointmentInfo = undefined, alreadyBooked = false) => {
      window.removeEventListener('consultation-booked', bookingEventlistener);
      window.dispatchEvent(new CustomEvent('consultation-booked', { detail: { appointment: appointmentInfo, alreadyBooked } }));
    };

    const enableButton = eventType => {
      if (errorOnSubmit) {
        if ((eventType == 'input' && errorOnSubmit == 'email') || (eventType == 'change' && errorOnSubmit == 'taken')) {
          submitted = false;
          errorOnSubmit = false;

          if (alerts.taken.style.display === 'block') {
            slideUp(alerts.taken);
            boxHeight();
          }

          if (alerts.email.style.display === 'block') {
            slideUp(alerts.email);
            boxHeight();
          }

          refreshSubmitState();
        }
      }
    };

    [timeSelect, dateSelect].forEach(el => {
      el.addEventListener('change', event => {
        enableButton(event.type);
        refreshSubmitState();
      });
    });

    submit.addEventListener('click', async ev => {
      ev.preventDefault();
      if (blockedReferrer) {
        updateResultSlide();
        formLibrary.switchSlide(resultSlide);
        return;
      }
      const validationResult = validate.validateAll(appointmentSlide);

      if (
        submitted ||
        (!allowCallbackOnly && validationResult.length) ||
        (allowCallbackOnly && !Array.from(validationResult).every(field => field.classList.contains('dateSelect') || field.classList.contains('timeSelect')))
      ) {
        notificationHandler.setValidateError(validationResult, submit);
        return;
      }

      if (isEmailRejected || isIncomeTooLow) {
        return;
      }

      submitted = true;
      consultationForm.classList.add('submitted');

      const expatTel = consultationForm.querySelector('.iti .otto-prefix');
      const selected = timeSelect.options[timeSelect.selectedIndex];
      const svgAnimationWatch = resultSlide.querySelector('.form-slide.result-slide .minutes svg .circle.cfill animate');

      // assemble contact details
      const contactDetailsDTO = serializeForm(consultationForm, expatTel ? expatTel.innerHTML : null);
      contactDetailsDTO.landing_page_language = language;
      if (customerType) {
        contactDetailsDTO.customer_type = customerType;
      }
      const payload = { contact_details: contactDetailsDTO };

      // add appointment
      if (!isEmailRejected && !isIncomeTooLow && selected.value !== '' && selected.value !== 'noday') {
        payload.appointment = {
          start: selected.dataset.start,
          end: selected.dataset.end
        };
      }
      // add calendar type
      payload.calendar = calendarType;

      // add utmz or utmi values if present
      await attachTrackingPayload(payload);

      const previousAppointment = previousAppointmentEntryPromise instanceof Promise ? await previousAppointmentEntryPromise : previousAppointmentEntryPromise;

      // check if the user has already booked a consultation
      if (
        (previousAppointment?.type === 'callback' && !payload?.appointment) ||
        ((previousAppointment?.status === AppointmentStatus.Pending || previousAppointment?.status === AppointmentStatus.Confirmed) && dayjs(previousAppointment?.start).isAfter(dayjs()))
      ) {
        const appointmentResponse = { appointment_details: previousAppointment };
        updateResultSlide(appointmentResponse, true);
        notifyForms(appointmentResponse, true);
        formLibrary.switchSlide(resultSlide);

        submit.textContent = en ? 'Already booked' : 'Bereits gebucht';
        submit.disabled = true;
        return;
      }

      const linkText = submit.innerText;
      submit.innerHTML = progressText;

      try {
        const leadDimensions = getPhoneLeadDimensions(userProfile);
        payload.contact_details.insurance_group = leadDimensions.insurance_group;
        const response = await postToLeadWithCaptcha('/protected/consultation', payload, {
          captcha: captchaController,
          requireCaptcha: requireCaptchaOnSubmit
        });
        if (response instanceof Error) {
          throw response;
        }

        submit.textContent = en ? 'Sent' : 'Abgeschickt';
        submit.disabled = true;
        let doi_status = 'pending';
        if (response.consent_confirmed) {
          doi_status = 'validated';
        } else if (response.appointment_details?.doi_enabled === false) {
          doi_status = 'disabled';
        }
        const leadLanguage = languageSelectRadios?.length ? Array.from(languageSelectRadios).find(radio => radio.checked)?.value : language;
        await pushToDataLayer({
          event: 'phone_lead',
          element_section: elementSection,
          link_text: linkText,
          consultation_type: payload.appointment ? 'appointment' : 'callback',
          income: 'na',
          doi_status,
          jaeg_reached: 'na',
          leadsUserData: {
            email_address: payload.contact_details.email,
            phone_number: payload.contact_details.phone_number,
            address: {
              first_name: payload.contact_details.first_name,
              last_name: payload.contact_details.last_name
            }
          },
          lead_language: leadLanguage,
          ...leadDimensions
        });
        metrics.count('consultation.phone_lead', 1, {
          attributes: {
            lead_language: leadLanguage,
            metric_source: blockType,
            user_agent: navigator.userAgent,
            uri: window.location.href,
            ...getPageDimensions()
          }
        });
        //save mail and appointment time in local storage for double booking check and show success modal
        setPreviousAppointment(response.appointment_details?.id, payload.contact_details.email, payload.contact_details.phone_number, response.consent_confirmed, !response.appointment_details?.id);

        if (response) {
          updateResultSlide(response, false);
          notifyForms(response.appointment_details);
        } else {
          updateResultSlide();
          notifyForms();
        }
        //start SVG Animation on resultSlide
        if (svgAnimationWatch) {
          setTimeout(() => {
            svgAnimationWatch.beginElement();
          }, 500);
        }
        formLibrary.switchSlide(resultSlide);
      } catch (error) {
        console.error(error);
        switch (error.message) {
          case 'taken':
            slideDown(alerts.taken);
            notificationHandler.setAriaMessage(alerts.taken.textContent, [dateSelect, timeSelect], submit);
            consultationForm.classList.remove('submitted');
            submit.innerHTML = originalSubmitContent;
            submit.disabled = true;
            boxHeight();
            errorOnSubmit = 'taken';
            break;
          case 'error: contact_details.email must be an email':
            notificationHandler.setAriaMessage(alerts.email.textContent, [emailInput], submit);
            slideDown(alerts.email);
            consultationForm.classList.remove('submitted');
            submit.innerHTML = originalSubmitContent;
            submit.disabled = true;
            errorOnSubmit = 'email';
            boxHeight();
            break;
          default:
            updateBookingFailedSlide();
        }
      }
    });
  }

  const updateRejectedSlide = formValues => {
    rejectedEnSlide.querySelector('.first-name').innerText = formValues['first_name'];
    rejectedEnSlide.querySelector('.employment-only').style.display = formValues.employment === 'employed' ? 'block' : 'none';
    formLibrary.switchSlide(rejectedEnSlide, false);
    pushToDataLayer({
      event: 'lead_rejected',
      rejection_reason: 'income',
      element_section: elementSection,
      ...getLeadDimensions(userProfile)
    });
  };

  const updateRejectedStudentSlide = formValues => {
    rejectedStudentSlide.querySelector('.first-name').innerText = formValues['first_name'];
    formLibrary.switchSlide(rejectedStudentSlide, false);
    pushToDataLayer({
      event: 'lead_rejected',
      rejection_reason: 'age',
      element_section: elementSection,
      ...getLeadDimensions(userProfile)
    });
  };

  const updateRejectedTooYoungSlide = () => {
    formLibrary.switchSlide(rejectedTooYoungSlide, false);

    pushToDataLayer({
      event: 'lead_rejected',
      rejection_reason: 'age',
      element_section: elementSection,
      ...getLeadDimensions(userProfile)
    });
  };

  const updateRejectedTopUpSlide = interest => {
    let signupLink = `https://${window.location.host}/v/zahnzusatzversicherung#tarife`;
    if (interest === 'clinic') {
      signupLink = `https://${window.location.host}/signup-app/clinic`;
    } else if (en) {
      signupLink = `https://${window.location.host}/en/v/dental-insurance-plan#tarife`;
    }

    let signupText = 'Tarif auswählen';
    if (interest === 'clinic') {
      signupText = en ? 'Start signup' : 'Direkt zum Antrag';
    } else if (interest === 'dental' && en) {
      signupText = 'Select a tariff';
    }

    userProfile = {
      ...userProfile,
      insurance_category: getCategoryFromInterest(interest)
    };
    rejectedTopupSlide.querySelector('.signup-link').setAttribute('href', signupLink);
    rejectedTopupSlide.querySelector('.signup-link').innerText = signupText;
    formLibrary.switchSlide(rejectedTopupSlide, false);
    pushToDataLayer({
      event: 'lead_rejected',
      element_section: elementSection,
      ...getLeadDimensions(userProfile)
    });
  };
  const updateBookingFailedSlide = () => {
    formLibrary.switchSlide(bookingFailedSlide, false);
    pushToDataLayer({
      event: 'error',
      element_section: elementSection,
      error_type: 'lead_submission_failed'
    });
  };

  async function handleCalendarLoad(calendarType, dateSelect, timeSelect, requestTimeout, timeout) {
    const submit = appointmentSlide.querySelector('.btn-submitter');
    const initSubmitText = submit.innerHTML;

    if (!appointmentGroup || !submit) {
      return;
    }

    languageSelect?.classList.add('fetching-appointments');
    submit.innerHTML = `<span>${en ? 'Loading appointments' : 'Termine werden geladen'}<span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`;
    appointmentGroup.classList.add('is-disabled');

    if (requestTimeout) {
      await new Promise(resolve => setTimeout(resolve, timeout));
    }

    try {
      await loadCalendar(calendarType, dateSelect, timeSelect);
      submit.innerHTML = initSubmitText;
      languageSelect?.classList.remove('fetching-appointments');
      appointmentGroup.classList.remove('is-disabled');
      failedToFetchAppointments = false;
    } catch (error) {
      failedToFetchAppointments = true;
      allowCallbackOnly = true;
      languageSelect?.classList.remove('fetching-appointments');
      const container = appointmentSlide.querySelector('.appointment-container-wrapper');
      const dateAndTimeFieldset = appointmentSlide.querySelector('.appointment-container');
      dateAndTimeFieldset.classList.add('hide');
      const height = dateAndTimeFieldset.offsetHeight;
      container.style.height = height + 'px';
      const callbackText = appointmentSlide.querySelector('.callback-text ');
      callbackText.classList.remove('is-hidden');
      callbackText.classList.add('show');
      setTimeout(() => dateAndTimeFieldset.classList.add('is-hidden'), 300);
      submit.innerHTML = `${en ? 'Call me back' : 'Rückruf anfordern'} ${arrowSvg}`;
    }
  }
});
